import { create } from "zustand";
import {
  isMessageOverEvent,
  MessageOverEvent,
} from "@/modules/types/conversation/message";
import {
  isNotificationMessageDto,
  NotificationMessageDto,
} from "@/modules/types/notification";
import {
  isJobRubricExtractionEvent,
  JobRubricExtractionEvent,
} from "@/modules/types/job/jobScoring";
import { sseService } from "../services/realtime/sseService";
import {
  EventSourcePolyfill,
  EventListener as PolyfillEventListener,
  Event,
} from "event-source-polyfill";
import { AppError } from "@/lib/utils/errors";

let currentEventSource: EventSourcePolyfill | null = null;

export interface SseMessage {
  type: string;
  data: NotificationMessageDto | MessageOverEvent | JobRubricExtractionEvent;
  id: string;
}

interface SseState {
  isConnected: boolean;
  lastEventId: string | null;
  errorMessage: string | null;
  messages: SseMessage[];
  isConnecting: boolean;
  newMessageCount: number;
}

interface SseActions {
  connect: () => void;
  connected: () => void;
  disconnect: () => void;
  eventReceived: (message: SseMessage) => void;
  error: (errorMessage: string) => void;
  clearMessages: () => void;
  popMessage: (messageId: string) => void;
  popLatestMessage: () => void;
  connectSSEAsync: (payload: {
    url: string;
    lastEventId?: string | null;
  }) => Promise<void>;
  disconnectSSEAsync: () => void;
}

export type SseStore = SseState & SseActions;

const initialState: SseState = {
  isConnected: false,
  lastEventId: null,
  errorMessage: null,
  messages: [],
  isConnecting: false,
  newMessageCount: 0,
};

export const useSseStore = create<SseStore>((set, get) => ({
  ...initialState,

  connect: () =>
    set({
      isConnected: false,
      errorMessage: null,
      isConnecting: true,
    }),

  connected: () =>
    set({
      isConnected: true,
      errorMessage: null,
      isConnecting: false,
    }),

  disconnect: () => {
    if (currentEventSource) {
      currentEventSource.close();
      currentEventSource = null;
    }
    set({
      isConnected: false,
      errorMessage: null,
      isConnecting: false,
      newMessageCount: 0,
    });
  },

  eventReceived: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
      newMessageCount: state.newMessageCount + 1,
      lastEventId: message.id || state.lastEventId,
      errorMessage: null,
      isConnecting: false,
    })),

  error: (errorMessage) =>
    set({
      isConnected: false,
      errorMessage,
      isConnecting: false,
    }),

  clearMessages: () => set({ messages: [] }),

  popMessage: (messageId) =>
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== messageId),
      newMessageCount: Math.max(0, state.newMessageCount - 1),
    })),

  popLatestMessage: () =>
    set((state) => {
      if (state.messages.length === 0) return state;
      const newMessages = [...state.messages];
      newMessages.pop();
      return {
        messages: newMessages,
        newMessageCount: Math.max(0, state.newMessageCount - 1),
      };
    }),

  connectSSEAsync: async ({ url, lastEventId }) => {
    const { connected, error, disconnect, eventReceived } = get();

    if (currentEventSource) {
      currentEventSource.close();
      currentEventSource = null;
    }

    set({ isConnecting: true, isConnected: false, errorMessage: null });

    try {
      const eventSourceOptions = {
        headers: {
          Authorization: `Bearer ${sseService.getToken()}`,
        },
      };

      currentEventSource = new EventSourcePolyfill(url, eventSourceOptions);

      currentEventSource.onopen = () => {
        connected();
      };

      currentEventSource.onerror = (event) => {
        const appErr = new AppError(event);
        appErr.logError("currentEventSource.onerror");
        error("SSE connection error");
        if (currentEventSource) {
          currentEventSource.close();
        }
        currentEventSource = null;
        disconnect();
      };

      currentEventSource.onmessage = (event) => {
        try {
          const parsedData = JSON.parse(event.data);
          const data = parsedData as
            | NotificationMessageDto
            | MessageOverEvent
            | JobRubricExtractionEvent;
          let eventType = "";
          if (isJobRubricExtractionEvent(data)) {
            eventType = "job_rubric_extraction";
          } else if (isNotificationMessageDto(data)) {
            eventType = "notification";
          } else if (isMessageOverEvent(data)) {
            eventType = "new_message";
          }
          const messageId =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
          if (data && eventType.length > 0) {
            eventReceived({
              type: eventType,
              data: data,
              id: event.lastEventId || messageId,
            });
          }
        } catch (e) {
          const appErr = new AppError(e);
          appErr.logError("currentEventSource.onmessage.parsing");
        }
      };

      return new Promise<void>((resolve, reject) => {
        if (!currentEventSource) {
          reject("EventSource not initialized");
          disconnect();
          return;
        }
        const initialErrorListener: PolyfillEventListener = (event: Event) => {
          if (currentEventSource) {
            currentEventSource.removeEventListener(
              "error",
              initialErrorListener
            );
            currentEventSource.removeEventListener("open", initialOpenListener);
            currentEventSource.close();
          }
          currentEventSource = null;
          error("SSE initial connection failed");
          reject("SSE initial connection failed");
          disconnect();
        };
        const initialOpenListener = () => {
          if (!currentEventSource) return;
          currentEventSource.removeEventListener("error", initialErrorListener);
          currentEventSource.removeEventListener("open", initialOpenListener);
          resolve();
        };

        currentEventSource.addEventListener("error", initialErrorListener);
        currentEventSource.addEventListener("open", initialOpenListener);
      });
    } catch (err) {
      if (currentEventSource) {
        currentEventSource.close();
        currentEventSource = null;
      }
      error("Failed to initialize SSE connection");
      disconnect();
      throw err;
    }
  },

  disconnectSSEAsync: () => {
    get().disconnect();
  },
}));
